class BlogReceipts extends HTMLElement {
  constructor() {
    super();

    this.recipesContainer = null;
    this.loadMoreBtn = null;
    this.baseUrlTemplate = '';
    this.baseUrl = '';
    this.recipesFilters = null;
    this.flavorFilterWrap = null;
    this.opacityBg = null;
    this.closeFlavorFilter = null;
    this.applyFilterBtn = null;
    this.pageTitle = null;
    this.buttonTitle = null;
    this.currentPage = 1;
    this.totalPages = 1;
    this.isLoading = false;
    this.lastFocusedElement = null;
    this.focusableElements = null;
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;
    this.filterButton = null;
    this.isKeyboardUser = false;
  }

  connectedCallback() {
    this.initializeElements();
    this.baseUrlTemplate = this.dataset.baseUrl || '';
    this.baseUrl = this.baseUrlTemplate;

    if (!this.recipesContainer || !this.loadMoreBtn ) {
      console.error('Required elements not found');
      return;
    }

    this.attachEventListeners();
    this.loadRecipes(1);
  }

  initializeElements() {
    this.recipesContainer = this.querySelector('.recipes-page__recipes-container');
    this.loadMoreBtn = this.querySelector('.recipes-page__load-more');
    this.recipesFilters = this.querySelector('.recipes-page__filters-wrapper');
    this.flavorFilterWrap = this.querySelector('.flavor-filter-modal');
    this.opacityBg = this.querySelector('.flavor-filter-modal__overlay');
    this.closeFlavorFilter = this.querySelector('.flavor-filter-modal__close');
    this.filterSelects = this.querySelectorAll('.flavor-filter-modal__select');
    this.applyFilterBtn = this.querySelector('.flavor-filter-modal__submit');
    this.pageTitle = this.querySelector('.recipes-page__results');
    this.buttonTitle = this.querySelector('.recipes-page__button-title');
    this.buttonTitleSecondary = this.querySelector('.recipes-page__button-container.secondary .recipes-page__button-title');
    this.recipesLoader = this.querySelector('.recipes-page__loader');
    this.noMorePostsContainer = this.querySelector('.recipes-page__message-no-more');
    this.noPostsFoundContainer = this.querySelector('.recipes-page__message-error');
    this.filterButton = this.querySelector('.recipes-page__flavor-button');
  }

  attachEventListeners() {
    document.addEventListener('mousedown', () => {
      this.isKeyboardUser = false;
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Tab' || e.key === 'Enter' || e.key === ' ') {
        this.isKeyboardUser = true;
      }
    });

    if (this.recipesFilters) {
      this.recipesFilters.addEventListener('click', () => this.showFilterModal());
    }

    if (this.filterButton) {
      this.filterButton.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.showFilterModal();
        }
      });
    }

    if (this.opacityBg) {
      this.opacityBg.addEventListener('click', () => this.hideFilterModal());
    }

    if (this.closeFlavorFilter) {
      this.closeFlavorFilter.addEventListener('click', () => this.hideFilterModal());
      this.closeFlavorFilter.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.hideFilterModal();
        }
      });
    }

    if (this.applyFilterBtn) {
      this.applyFilterBtn.addEventListener('click', (e) => this.handleApplyFilter(e));
    }

    if (this.loadMoreBtn) {
      this.loadMoreBtn.addEventListener('click', () => this.handleLoadMore());
    }
  }

  showFilterModal() {
    if (this.flavorFilterWrap) {
      this.lastFocusedElement = document.activeElement;
      this.flavorFilterWrap.style.display = 'flex';

      if (this.isKeyboardUser) {
        this.flavorFilterWrap.classList.add('keyboard-mode');
      } else {
        this.flavorFilterWrap.classList.remove('keyboard-mode');
      }

      if (this.filterButton) {
        this.filterButton.setAttribute('aria-expanded', 'true');
      }

      this.setupFocusTrap();

      if (this.closeFlavorFilter) {
        this.closeFlavorFilter.focus();
      }
    }
  }

  hideFilterModal() {
    if (this.flavorFilterWrap) {
      this.flavorFilterWrap.style.display = 'none';

      if (this.filterButton) {
        this.filterButton.setAttribute('aria-expanded', 'false');
      }

      this.removeFocusTrap();

      if (this.lastFocusedElement) {
        this.lastFocusedElement.focus();
      }
    }
  }

  setupFocusTrap() {
    const focusableSelectors = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    this.focusableElements = this.flavorFilterWrap.querySelectorAll(focusableSelectors);

    if (this.focusableElements.length > 0) {
      this.firstFocusableElement = this.focusableElements[0];
      this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];
    }

    this.handleKeyDown = this.handleKeyDown.bind(this);
    document.addEventListener('keydown', this.handleKeyDown);
  }

  removeFocusTrap() {
    if (this.handleKeyDown) {
      document.removeEventListener('keydown', this.handleKeyDown);
    }
  }

  handleKeyDown(e) {
    if (!this.flavorFilterWrap || this.flavorFilterWrap.style.display === 'none') {
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      this.hideFilterModal();
      return;
    }

    const activeElement = document.activeElement;
    const isSelect = activeElement && activeElement.tagName === 'SELECT';
    const isSelectExpanded = isSelect && activeElement.classList.contains('select-expanded');

    if (e.key === 'Enter' && isSelect) {
      e.preventDefault();

      if (activeElement.size <= 1) {
        activeElement.size = activeElement.options.length;
        activeElement.classList.add('select-expanded');
      } else {
        activeElement.size = 1;
        activeElement.classList.remove('select-expanded');
      }
      return;
    }

    if (e.key === 'Tab') {
      if (isSelectExpanded) {
        e.preventDefault();
        const currentIndex = activeElement.selectedIndex;
        const optionsCount = activeElement.options.length;

        if (e.shiftKey) {
          if (currentIndex > 0) {
            activeElement.selectedIndex = currentIndex - 1;
          }
        } else {
          if (currentIndex < optionsCount - 1) {
            activeElement.selectedIndex = currentIndex + 1;
          }
        }
      }

      if (this.focusableElements.length === 0) {
        e.preventDefault();
        return;
      }

      if (e.shiftKey) {
        if (activeElement === this.firstFocusableElement) {
          e.preventDefault();
          this.lastFocusableElement.focus();
        }
      } else {
        if (activeElement === this.lastFocusableElement) {
          e.preventDefault();
          this.firstFocusableElement.focus();
        }
      }
    }
  }

  handleApplyFilter(e) {
    e.preventDefault();

    const selectedFilters = [];
    const selectedLabels = [];

    this.filterSelects.forEach(select => {
      const value = select.value;
      const text = select.options[select.selectedIndex].text;

      if (value) {
        selectedFilters.push(value);
        selectedLabels.push(text);
      } else {
        selectedLabels.push(text);
      }
    });

    if (selectedFilters.length > 0) {
      this.baseUrl = this.baseUrlTemplate + '+' + selectedFilters.join('+');
    } else {
      this.baseUrl = this.baseUrlTemplate;
    }

    if (this.filterSelects.length === 2) {
      const firstSelect = this.filterSelects[0];
      const secondSelect = this.filterSelects[1];

      const firstText = firstSelect.options[firstSelect.selectedIndex].text;
      const secondText = secondSelect.options[secondSelect.selectedIndex].text;

      if (this.buttonTitle) {
        this.buttonTitle.textContent = firstText;
      }

      if (this.buttonTitleSecondary) {
        this.buttonTitleSecondary.textContent = secondText;
      }

      const displayText = selectedFilters.length > 0 ? selectedLabels.filter((_, index) => {
        const select = this.filterSelects[index];
        return select.value !== '';
      }).join(' ') : 'All Recipes';

      if (this.pageTitle) {
        this.pageTitle.textContent = selectedFilters.length > 0 ? displayText + ' Recipes' : 'All Recipes';
      }
    } else {
      const displayText = selectedLabels.length > 0 ? selectedLabels.join(' ') : 'All Flavors';

      if (this.buttonTitle) {
        this.buttonTitle.textContent = displayText;
      }

      if (this.pageTitle) {
        this.pageTitle.textContent = selectedFilters.length > 0 ? displayText + ' Recipes' : 'All Recipes';
      }
    }

    this.hideNoPostsFoundContainers();
    this.currentPage = 1;
    this.loadRecipes(1, false);
    this.hideFilterModal();
  }

  handleLoadMore() {
    this.currentPage++;
    this.loadRecipes(this.currentPage, true);
  }

  showNoMorePosts() {
    if (this.noMorePostsContainer) {
      this.noMorePostsContainer.style.display = 'block';
    }
  }

  hideNoMorePosts() {
    if (this.noMorePostsContainer) {
      this.noMorePostsContainer.style.display = 'none';
    }
  }

  hideNoPostsFoundContainers() {
    if (this.noPostsFoundContainer) {
      this.noPostsFoundContainer.style.display = 'none';
    }
  }

  loadRecipes(page, append = false) {
    if (this.isLoading) return;

    this.isLoading = true;

    const url = page === 1 ? this.baseUrl : `${this.baseUrl}?page=${page}`;

    fetch(url)
        .then(response => {
          if (!response.ok) {
            if (response.status === 404) {
              if (this.noPostsFoundContainer) {
                this.noPostsFoundContainer.style.display = 'block';
                this.noMorePostsContainer.style.display= 'none';
              }
              return 'null';
            }
            throw new Error('Network response was not ok: ' + response.status);
          }
          return response.text();
        })
        .then(html => {

          if (html === '') return;

          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const mainArticle = doc.querySelector('.main-article');

          if (mainArticle) {
            const paginationWrapper = mainArticle.querySelector('.wt-pagination-wrapper');
            if (paginationWrapper) {
              paginationWrapper.remove();
            }

            const fetchedPaginationInput = doc.getElementById('articles-pagination');
            if (fetchedPaginationInput && fetchedPaginationInput.value) {
              this.totalPages = parseInt(fetchedPaginationInput.value, 10);
            }

            const hasGridItems = doc.querySelector('.main-blog__grid__item');

            if (!hasGridItems) {
              if (this.noPostsFoundContainer) {
                this.noPostsFoundContainer.style.display = 'block';
              }
              this.recipesContainer.innerHTML = '';
              this.loadMoreBtn.style.display = 'none';
              this.hideNoMorePosts();
              if (this.recipesLoader) {
                this.recipesLoader.style.display = 'none';
              }
              this.isLoading = false;
              return;
            }

            if (append) {
              this.recipesContainer.insertAdjacentHTML('beforeend', mainArticle.innerHTML);
            } else {
              this.recipesContainer.innerHTML = mainArticle.innerHTML;
            }

            if (this.recipesLoader) {
              this.recipesLoader.style.display = 'none';
            }

            const hasRecipes = this.recipesContainer.children.length > 0;

            if (this.currentPage < this.totalPages) {
              this.loadMoreBtn.style.display = 'block';
              this.loadMoreBtn.disabled = false;
              this.hideNoMorePosts();
            } else {
              this.loadMoreBtn.style.display = 'none';
              if (hasRecipes && (this.currentPage >= this.totalPages || this.totalPages === 1)) {
                this.showNoMorePosts();
              } else {
                this.hideNoMorePosts();
              }
            }
          } else {
            console.error('Main article container not found in fetched content');
            this.recipesContainer.innerHTML = '';
            this.loadMoreBtn.style.display = 'none';
            if (this.recipesLoader) {
              this.recipesLoader.style.display = 'none';
            }
          }

          this.isLoading = false;
        })
        .catch(error => {
          console.error('Error fetching recipes:', error);
          this.recipesContainer.innerHTML = '';
          this.loadMoreBtn.disabled = false;
          if (this.recipesLoader) {
            this.recipesLoader.style.display = 'none';
          }
          this.isLoading = false;
        });
  }
}

customElements.define('blog-receipts', BlogReceipts);